import { answerCoverage } from "./BriefingParts";
import type { PlanFile } from "../../Types/AccountPlan";
import { STALLED_LINE } from "../../Utils/AccountPlans/queueText";

/**
 * The Briefing Questions' state for one version, from one source (UI tour item 5,
 * 2026-11-03): the run summary, the Review briefing parts, the Plan tab card and the
 * Briefing Questions tab all read this, so one tab can't say "5 answered" while
 * another says "No answers yet".
 *
 * - waiting: the briefing isn't written yet, so no answers are saved (even when the
 *   run's questions pass already returned; they are saved with the briefing).
 * - stalled: our failure (the run stalled, or the questions pass failed past its
 *   retries). Reads as STALLED_LINE, never as failed (P8).
 * - none: a briefing written before Briefing Questions existed.
 * - answered: answers saved; the split is known when the file carries them.
 */
export type BqStatus = "waiting" | "stalled" | "none" | "answered";

export interface BqSummary {
  status: BqStatus;
  total: number;
  answered: number;
  partial: number;
  missing: number;
  /** answered / partial / missing are known (the file carries its answers). */
  known: boolean;
}

const EMPTY = { total: 0, answered: 0, partial: 0, missing: 0, known: false };

/** The briefing run stalled on our failure (backend jobs._stall_plan sets progress.stalled). */
export const briefingStalled = (file?: PlanFile | null): boolean =>
  !!file &&
  file.sponsor_view_status !== "ready" &&
  // ui-a's server flag (is_briefing_stalled), declared with its contract change.
  (!!(file as { briefing_stalled?: boolean }).briefing_stalled ||
    file.sponsor_view_status === "failed" ||
    file.progress?.stalled?.kind === "sponsor_view" ||
    file.progress?.stalled?.kind === "admin_sponsor_view");

export const bqSummary = (file?: PlanFile | null): BqSummary => {
  if (!file) return { status: "waiting", ...EMPTY };
  if (file.sponsor_view_status !== "ready")
    return {
      status: briefingStalled(file) ? "stalled" : "waiting",
      ...EMPTY,
    };
  const view = file.sponsor_view;
  if (view) {
    if (view.briefing_status === "failed")
      return { status: "stalled", ...EMPTY };
    const answers = view.briefing?.answers || [];
    if (!answers.length) return { status: "none", ...EMPTY };
    const out = { ...EMPTY, status: "answered" as const, known: true };
    answers.forEach(a => {
      out[answerCoverage(a)] += 1;
    });
    return { ...out, total: answers.length };
  }
  // A list payload (no sponsor_view): the run's own record of the questions pass.
  const step = file.progress?.briefing?.steps?.questions;
  if (step?.state === "failed") return { status: "stalled", ...EMPTY };
  const n = step?.items?.length ?? 0;
  if (step?.state === "done" && n)
    return { status: "answered", ...EMPTY, total: n, answered: n };
  return { status: "none", ...EMPTY };
};

const FLOW_BQ = "Briefing Questions";

const qs = (n: number) => `${n} Briefing Question${n === 1 ? "" : "s"}`;

/**
 * "5 Briefing Questions answered", "5 Briefing Questions · 3 answered · 1 partly ·
 * 1 missing from the plan", "Briefing Questions: Taking longer than usual · …".
 * Empty when there is nothing to say (no answers yet, or none for this briefing).
 */
export const bqCountText = (s: BqSummary): string => {
  if (s.status === "stalled") return `${FLOW_BQ}: ${STALLED_LINE}`;
  if (s.status !== "answered" || !s.total) return "";
  if (!s.known || (s.partial === 0 && s.missing === 0))
    return `${qs(s.total)} answered`;
  return [
    qs(s.total),
    s.answered && `${s.answered} answered`,
    s.partial && `${s.partial} partly`,
    s.missing && `${s.missing} missing from the plan`,
  ]
    .filter(Boolean)
    .join(" · ");
};

/** The Briefing Questions tab and cards while there is nothing to show yet. */
export const BQ_WAITING = "Answers appear after the briefing is written";

/** Matches the stand-in model's test markers ("[[standin:fail:sponsor_view]]"). */
const MARKER = /\[\[[^[\]]*\]\]/g;
const HAS_MARKER = /\[\[[^[\]]*\]\]/;

/**
 * Slide text as people should read it: internal [[…]] markers removed (UI tour item
 * 10), with the blank lines they leave behind.
 */
export const cleanSlideText = <T extends string | null | undefined>(
  text: T
): T => {
  if (typeof text !== "string" || !text.includes("[[")) return text;
  return text
    .split("\n")
    .map(line => ({
      had: HAS_MARKER.test(line),
      line: line.replace(MARKER, ""),
    }))
    .filter(({ had, line }) => !had || line.trim() !== "")
    .map(({ line }) => line)
    .join("\n")
    .trim() as T;
};
